/*
 * Copyright (c) 2023 Puratos. All rights reserved.
 */

import * as React from 'react';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { addProductToCart } from '../../../actions/products';
import { useDispatch, useSelector } from 'react-redux';
import { addToCartAction, isLoadingById } from '../../../selectors/loader';
import {
    getAddedQuantityForSku,
    selectSKUPromotion,
} from '../../../selectors/products';
import { ShowHide } from '../../atoms/layout/ShowHide';
import { useI18N } from '../../hoc/withI18N';
import { QuantityInput } from '../../atoms/forms/QuantityInput';
import { AddToCartButton } from '../../atoms/bootstrap/Button';
import { GTMContext } from '../../../analytics/ga4/utils/GTMContext';
import {
    getActiveSku,
    getActiveSkuFromVariations,
    Store,
} from '../../../selectors/productPage';
import { StockInformation } from '../../atoms/details/StockInformation';
import { IECOMVariationActions } from '../../../interfaces/organisms/product-page/IECOMVariatonActions';
import {
    IPackaging,
    IPromotion,
} from '../../../interfaces/my-puratos/IGeneric';
import { openModal } from '../../../actions/modals';
import { AddedToCartModalId } from '../../my-puratos/organisms/modal/AddedToCartModal';
import { googleEvents } from '../../../utils/googleEvents';
import {
    selectMyPuratosAccountEmail,
    selectUserId,
} from '../../../selectors/auth';
import { getLanguageIso } from '../../../selectors/generic';
import { isLoggedIn } from '../../../data/user.model';
import {getCurrencyCode, getSubTotalPrice} from '../../../selectors/cart';
import { productEventParseAdd } from '../../../analytics/ga4/parsers/item';
import { interactionCrossSellMapper, interactionPageMapper } from '../../../analytics/ga4/utils/mapper';
import { GA4LoginType } from '../../../analytics/ga4/types/login';
import { setProductCrossSell } from '../../../actions/analytics';

export const ECOMVariationActions: FunctionComponent<IECOMVariationActions> = ({
    variations,
}) => {
    const [quantity, setQuantity] = useState(1);
    const [response, setResponse] = useState(false);

    const translate = useI18N();
    const dispatch = useDispatch();
    const context = useContext(GTMContext);

    const activeSku: IPackaging = useSelector(getActiveSku);
    const cartQuantity: number = useSelector((state) =>
        getAddedQuantityForSku(state, activeSku?.materialNumber)
    );
    const isProductLoading: boolean = useSelector((state: Store) =>
        isLoadingById(
            state,
            addToCartAction(
                getActiveSkuFromVariations(state, variations)?.materialNumber
            )
        )
    );
    const promotion: IPromotion = selectSKUPromotion(activeSku, null);
    const userId = useSelector(selectUserId);
    const userEmail = useSelector(selectMyPuratosAccountEmail);
    const currency = useSelector(getCurrencyCode);
    const subTotalPrice = useSelector(getSubTotalPrice);

    useEffect(() => {
        setQuantity(
            Math.max(1, activeSku?.minimumOrderableQuantity - cartQuantity) || 1
        );
    }, [activeSku]);

    const updateQuantity = (value) => {
        if (!isProductLoading) {
            setQuantity(value);
        }
    };

    const afterAddToCart = () => {
        const promotionData = {
            promotion_id: promotion ? promotion.id : '',
            promotion_name: promotion ? promotion.longPromoText : '',
        };

        dispatch(setProductCrossSell({ ...activeSku, type: interactionCrossSellMapper(context) }));

        googleEvents.addToCart({
            user_id: userId,
            page_language: getLanguageIso(),
            email_address: isLoggedIn() ? userEmail : '',
            login_status: userId ? GA4LoginType.LOGGED_IN : GA4LoginType.GUEST,
            website_section: interactionPageMapper(context),
            subTotalPrice: subTotalPrice,
            currency: currency,
            items: productEventParseAdd(
                activeSku,
                currency,
                interactionPageMapper(context),
                quantity,
                promotionData
            ),
        });
        dispatch(openModal(AddedToCartModalId, { sku: activeSku, quantity }));
        setQuantity(
            Math.max(1, activeSku?.minimumOrderableQuantity - cartQuantity) || 1
        );
        setResponse(true);
    };

    const dispatchAddToCart = () => {
        if (isProductLoading) {
            return;
        }

        setResponse(false);
        dispatch(
            addProductToCart(activeSku, quantity, context, afterAddToCart)
        );
    };

    return (
        <React.Fragment>
            <ShowHide show={activeSku?.hasPrice && !activeSku?.isObsolete}>
                <div className="product-page__pricing--actions">
                    <div className="product-page__pricing--actions-cart">
                        <div className="p-flex">
                            <QuantityInput
                                min={Math.max(
                                    1,
                                    activeSku?.minimumOrderableQuantity -
                                        cartQuantity
                                )}
                                disabled={isProductLoading}
                                updateValue={updateQuantity}
                                value={quantity}
                            />
                            <AddToCartButton
                                loading={isProductLoading}
                                disabled={
                                    !quantity ||
                                    isProductLoading ||
                                    activeSku?.stockType === 'BLOCKSALE'
                                }
                                onClick={dispatchAddToCart}
                                response={response}
                                text={translate('Add to cart')}
                            />
                        </div>

                        <StockInformation
                            type={activeSku?.stockType}
                            isProductPage
                        />
                    </div>
                    <ShowHide show={activeSku?.minimumOrderableQuantity > 1}>
                        <div className="product-page__pricing--actions-moq">
                            <span>
                                {translate(
                                    'The minimum order quantity for this product is {}'
                                ).replace(
                                    '{}',
                                    activeSku?.minimumOrderableQuantity
                                )}
                            </span>
                        </div>
                    </ShowHide>
                    <ShowHide show={!!promotion}>
                        <div
                            className={`product-page__pricing--actions-promo ${
                                promotion?.activated ? 'active' : ''
                            }`}
                        >
                            <div className={'promo-label p-margin-bottom-xs'}>
                                {promotion && promotion.promoCode}
                            </div>
                            <span>
                                {promotion &&
                                    (promotion.longPromoText ||
                                        promotion.shortPromoText ||
                                        activeSku.promoText)}{' '}
                                {promotion?.activated ? (
                                    <span
                                        className={
                                            'svg-check-mark-green-circle p-margin-left-xs'
                                        }
                                    />
                                ) : (
                                    ''
                                )}
                            </span>
                        </div>
                    </ShowHide>
                </div>
            </ShowHide>
        </React.Fragment>
    );
};
